import { ModelType } from '@tarslib/types/lib/common';
import { RequestMethodType } from '@tarslib/types/lib/httpApi';
import { FoxIconTypeSocketio, FoxIconWebsocket } from 'aui-biz-icons';
import clsx from 'clsx';
import React from 'react';
import type { ApiType } from './components/api-label/ApiLabel';
import { ApiLabel } from './components/api-label/ApiLabel';
import { ApiMethodIconSize } from './type/ApiMethodIconSize';

export type ApiMethodIconProps = {
  value:
    | {
        type: ModelType.HttpApi;
        method: RequestMethodType;
        apiType?: ApiType;
      }
    | {
        type: ModelType.WebSocketApi;
      }
    | {
        type: ModelType.CUSTOM_ENDPOINT;
        protocol: string;
      }
    | {
        type: ModelType.SOCKET_IO_API;
      };
  inverse?: boolean;
  size?: ApiMethodIconSize;
  apiLabelSize?: ApiMethodIconSize;
  disabledBackground?: boolean;
  className?: string;
  abbr?: boolean;
  style?: React.CSSProperties;
  onClick?: () => void;
  onDoubleClick?: () => void;
};

export function ApiMethodIcon(props: ApiMethodIconProps) {
  const {
    value,
    className,
    inverse,
    size = ApiMethodIconSize.Default,
    apiLabelSize = ApiMethodIconSize.Default,
    disabledBackground = false,
    abbr,
    style,
    onClick,
    onDoubleClick,
  } = props;

  const method = React.useMemo(() => {
    if (value.type === ModelType.WebSocketApi) {
      return undefined;
    } else if (value.type === ModelType.CUSTOM_ENDPOINT) {
      return abbr && !!value.protocol ? String(value.protocol).slice(0, 4) : value.protocol;
    } else if (value.type === ModelType.SOCKET_IO_API) {
      return undefined;
    } else {
      return abbr && !!value.method ? String(value.method).slice(0, 4) : value.method;
    }
  }, [value, abbr]);

  const apiType = React.useMemo(() => {
    if (value.type === ModelType.HttpApi) {
      return value.apiType;
    }
  }, [value]);

  return (
    <>
      {apiType && <ApiLabel size={apiLabelSize} apiType={apiType} />}
      {method && (
        <span
          className={clsx(
            'inline-flex items-center',
            {
              ['h-[14px] rounded-full px-1 py-0.5 text-xs font-700 leading-[10px]']:
                size === ApiMethodIconSize.Default,
              ['h-[24px] rounded-md px-1.5 py-[2px] text-sm font-600']:
                size === ApiMethodIconSize.Large,
            },
            !inverse
              ? clsx({
                  ['bg-green-1 text-green-6']: method === RequestMethodType.GET,
                  ['bg-orange-1 text-orange-6']: method === RequestMethodType.POST,
                  ['bg-blue-1 text-blue-6']: method === RequestMethodType.PUT,
                  ['bg-red-1 text-red-6']:
                    method === RequestMethodType.DELETE ||
                    method === RequestMethodType.OPTIONS ||
                    method === RequestMethodType.HEAD ||
                    method === RequestMethodType.WS,
                  ['bg-pink-1 text-pink-6']: method === RequestMethodType.PATCH,
                  ['bg-geekblue-1 text-geekblue-6']: method === RequestMethodType.TRACE,
                  ['bg-purple-1 text-purple-6']:
                    method === RequestMethodType.CONNECT || method === RequestMethodType.DOC,
                  ['bg-orange-1 text-orange-7']: method === RequestMethodType.DUBBO,
                  ['bg-magenta-1 text-magenta-6']: method === RequestMethodType.SOCKET,
                }) ||
                  clsx('text-purple-6', {
                    ['bg-purple-1']: value.type === ModelType.HttpApi,
                  })
              : clsx({
                  ['bg-green-6 text-white']: method === RequestMethodType.GET,
                  ['bg-orange-6 text-white']: method === RequestMethodType.POST,
                  ['bg-blue-6 text-white']: method === RequestMethodType.PUT,
                  ['bg-red-6 text-white']:
                    method === RequestMethodType.DELETE ||
                    method === RequestMethodType.OPTIONS ||
                    method === RequestMethodType.HEAD ||
                    method === RequestMethodType.WS,
                  ['bg-pink-6 text-white']: method === RequestMethodType.PATCH,
                  ['bg-geekblue-6 text-white']: method === RequestMethodType.TRACE,
                  ['bg-purple-6 text-white']:
                    method === RequestMethodType.CONNECT || method === RequestMethodType.DOC,
                  ['bg-orange-7 text-white']: method === RequestMethodType.DUBBO,
                  ['bg-magenta-6 text-white']: method === RequestMethodType.SOCKET,
                }) || clsx('bg-purple-6 text-white'),
            className,
            disabledBackground && '!bg-transparent',
          )}
          style={style}
          onClick={onClick}
          onDoubleClick={onDoubleClick}
        >
          <span className="truncate">{method.toUpperCase()}</span>
        </span>
      )}
      {value.type === ModelType.WebSocketApi && (
        <FoxIconWebsocket
          className={clsx('text-blue-6', className)}
          size={16}
          style={style}
          onClick={onClick}
          onDoubleClick={onDoubleClick}
        />
      )}

      {value.type === ModelType.SOCKET_IO_API && (
        <FoxIconTypeSocketio
          className={clsx('text-green-6', className)}
          size={16}
          style={style}
          onClick={onClick}
          onDoubleClick={onDoubleClick}
        />
      )}
    </>
  );
}
